/**
 * RowMenu: the "More" menu at the end of an invoice row, holding the
 * row's actions after its one primary (LP-34, the Invoices page workup).
 *
 * The menu holds the same controls the row used to show inline, with the
 * same test ids, gates and handlers: the menu changes where an action is
 * drawn, never what it does. Its items render only while it is open.
 *
 * Keyboard: the opener is a button with aria-haspopup="menu"; opening
 * moves focus to the first item, Up and Down move between items, Escape
 * closes and returns focus to the opener, and Tab closes it with focus
 * back on the opener. A press outside, or focus moving outside, closes it. The panel is an AnchoredLayer, so the table's
 * overflow wrapper never clips it.
 */

import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { AnchoredLayer } from '@/components/billing/AnchoredLayer';

export interface RowMenuProps {
  /** The opener's accessible name, e.g. "More for Fernmock, Isadora". */
  label: string;
  testId: string;
  /** The items. `close` closes the menu; call it before the item acts. */
  children: (close: () => void) => ReactNode;
}

const ITEMS = '[role="menuitem"]';

export function RowMenu({ label, testId, children }: RowMenuProps): ReactNode {
  const [open, setOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(ITEMS)?.focus({ preventScroll: true });
  }, [open]);

  const onListKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Tab') {
      // The panel is drawn at the end of the page, so a Tab out of it
      // would leave the table; focus goes back to the opener instead.
      e.preventDefault();
      close();
      openerRef.current?.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>(ITEMS) ?? []);
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    items[(at + step + items.length) % items.length]?.focus();
  };

  return (
    <>
      <Button
        ref={openerRef}
        variant="ghost"
        size="sm"
        data-testid={testId}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${testId}-list` : undefined}
        className="!px-1.5"
        onClick={() => setOpen((v) => !v)}
        icon={<MoreHorizontal size={16} aria-hidden="true" />}
      />
      {open && (
        <AnchoredLayer
          anchorRef={openerRef}
          onClose={close}
          id={`${testId}-list`}
          testId={`${testId}-list`}
          role="menu"
          ariaLabel={label}
          className="min-w-[11rem] rounded-base border border-sage-800/[8%] bg-paper py-1 shadow-sh3"
        >
          <div ref={listRef} onKeyDown={onListKey} className="flex flex-col items-stretch gap-0.5 px-1">
            {children(close)}
          </div>
        </AnchoredLayer>
      )}
    </>
  );
}
